import { useInfiniteQuery, useQueryClient } from '@tanstack/react-query';
import type { MessageListResponse } from '@chatapp-inbox/shared';
import { useMemo, useState } from 'react';
import { listMessages } from '../../api';
import { messagesKey } from '../conversation';
import { flattenMessages, trimToLatestPage, type MessagePages } from './messages-cache';

/** Mensagens por página (o servidor aceita até 100). */
export const PAGE_SIZE = 50;

/**
 * Histórico da conversa. A página 0 é a mais recente e `nextCursor` aponta para as mais antigas;
 * `messages` já vem em ordem cronológica e sem repetidas. Este é o cache que a T26 atualiza com
 * `setQueryData`: mantenha o formato.
 */
export function useChatMessages(conversationId: string) {
  const queryClient = useQueryClient();
  // Ao abrir, só a página mais recente é refeita (ver `trimToLatestPage`). Roda uma vez por
  // abertura, antes do primeiro uso do cache; é idempotente, então repetir não causa dano.
  useState(() => trimToLatestPage(queryClient, conversationId));

  const query = useInfiniteQuery<
    MessageListResponse,
    Error,
    MessagePages,
    ReturnType<typeof messagesKey>,
    string | null
  >({
    queryKey: messagesKey(conversationId),
    queryFn: ({ pageParam, signal }) =>
      listMessages({ conversationId, before: pageParam, limit: PAGE_SIZE, signal }),
    initialPageParam: null as string | null,
    getNextPageParam: (last) => last.nextCursor,
  });
  const data: MessagePages | undefined = query.data;
  const messages = useMemo(() => flattenMessages(data), [data]);
  return { query, messages };
}
